(() => {
  const ROOT_SELECTOR = '#MainContent[data-template="index"] .ivalo-ed__products--favorites';
  const CAROUSEL_THRESHOLD = 4;

  function carouselParts(panel) {
    const shell = panel?.querySelector?.('[data-devui-carousel]');
    const grid = shell?.querySelector?.('[data-devui-grid]');
    if (!shell || !grid) return null;

    return {
      shell,
      grid,
      prev: shell.querySelector('[data-devui-prev]'),
      next: shell.querySelector('[data-devui-next]'),
    };
  }

  function cardStep(grid) {
    const card = grid?.querySelector?.('.ivalo-ed__product-card');
    if (!card) return grid?.clientWidth || 0;

    const styles = window.getComputedStyle(grid);
    const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
    return card.getBoundingClientRect().width + gap;
  }

  function updateArrowState(parts) {
    if (!parts?.shell || !parts.grid) return;

    const enabled = parts.shell.classList.contains('is-carousel');
    if (!enabled) {
      if (parts.prev) parts.prev.disabled = true;
      if (parts.next) parts.next.disabled = true;
      return;
    }

    const maxScroll = Math.max(parts.grid.scrollWidth - parts.grid.clientWidth, 0);
    if (parts.prev) parts.prev.disabled = parts.grid.scrollLeft <= 2;
    if (parts.next) parts.next.disabled = parts.grid.scrollLeft >= maxScroll - 2;
  }

  function syncCarousel(panel) {
    const parts = carouselParts(panel);
    if (!parts) return;

    const productCount = parts.grid.querySelectorAll('.ivalo-ed__product-card').length;
    const enabled = productCount > CAROUSEL_THRESHOLD;

    parts.shell.classList.toggle('is-carousel', enabled);
    parts.grid.classList.toggle('is-carousel', enabled);
    parts.grid.dataset.productCount = String(productCount);

    if (!enabled) parts.grid.scrollLeft = 0;
    requestAnimationFrame(() => updateArrowState(parts));
  }

  function bindCarousel(panel) {
    const parts = carouselParts(panel);
    if (!parts || parts.shell.dataset.devuiCarouselBound === 'true') return;

    parts.shell.dataset.devuiCarouselBound = 'true';

    const move = (direction) => {
      if (!parts.shell.classList.contains('is-carousel')) return;
      const step = cardStep(parts.grid);
      if (!step) return;
      parts.grid.scrollBy({ left: direction * step, behavior: 'smooth' });
    };

    parts.prev?.addEventListener('click', () => move(-1));
    parts.next?.addEventListener('click', () => move(1));

    let scrollFrame = 0;
    parts.grid.addEventListener('scroll', () => {
      if (scrollFrame) cancelAnimationFrame(scrollFrame);
      scrollFrame = requestAnimationFrame(() => {
        updateArrowState(parts);
        scrollFrame = 0;
      });
    }, { passive: true });

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(() => updateArrowState(parts));
      resizeObserver.observe(parts.grid);
      parts.shell._devuiResizeObserver = resizeObserver;
    }

    syncCarousel(panel);
  }

  function setTabState(tabs, activeButton) {
    tabs.forEach((button) => {
      const active = button === activeButton;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-selected', active ? 'true' : 'false');
      button.tabIndex = active ? 0 : -1;
    });
  }

  function activateTab(section, button, tabs, panels, cta, focus = false) {
    const panelId = button?.dataset?.devuiPanelId;
    if (!panelId) return;

    const activePanel = panels.find((panel) => panel.id === panelId);
    if (!activePanel) return;

    setTabState(tabs, button);
    panels.forEach((panel) => {
      panel.hidden = panel !== activePanel;
    });

    const label = button.dataset.devuiLabel || button.textContent.trim();
    const collectionURL = button.dataset.devuiCollectionUrl || activePanel.dataset.devuiCollectionUrl;

    if (cta && collectionURL) {
      cta.href = collectionURL;
      cta.textContent = `SHOP ${String(label).toUpperCase()}`;
    }

    const parts = carouselParts(activePanel);
    if (parts) parts.grid.scrollLeft = 0;

    requestAnimationFrame(() => {
      syncCarousel(activePanel);
      updateArrowState(carouselParts(activePanel));
    });

    if (focus) button.focus();
  }

  function wireKeyboard(section, tabs, panels, cta) {
    const tablist = section.querySelector('.ivalo-ed__tabs');
    if (!tablist || !tabs.length) return;

    tablist.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;

      const current = Math.max(0, tabs.indexOf(document.activeElement));
      let next = current;

      if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;

      event.preventDefault();
      activateTab(section, tabs[next], tabs, panels, cta, true);
    });
  }

  function initFavorites(section) {
    if (!section || section.dataset.devuiFavoritesReady === 'true') return;

    const tabs = Array.from(section.querySelectorAll('.ivalo-ed__tabs [data-devui-panel-id]'));
    const panels = Array.from(section.querySelectorAll('[data-devui-panel]'));
    const cta = section.querySelector('[data-devui-favorites-cta]');

    if (!panels.length) return;
    section.dataset.devuiFavoritesReady = 'true';

    panels.forEach((panel) => bindCarousel(panel));

    if (tabs.length) {
      const activeButton = tabs.find((button) => button.getAttribute('aria-selected') === 'true') || tabs[0];
      tabs.forEach((button) => {
        button.addEventListener('click', () => activateTab(section, button, tabs, panels, cta));
      });
      wireKeyboard(section, tabs, panels, cta);
      activateTab(section, activeButton, tabs, panels, cta);
    } else {
      panels.forEach((panel, index) => {
        panel.hidden = index !== 0;
      });
      requestAnimationFrame(() => syncCarousel(panels[0]));
    }
  }

  function initAll(scope = document) {
    const sections = [];

    if (scope.matches?.(ROOT_SELECTOR)) sections.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((section) => sections.push(section));

    if (scope.matches?.('.ivalo-ed__products--favorites')) sections.push(scope);
    scope.querySelectorAll?.('.ivalo-ed__products--favorites').forEach((section) => {
      if (!sections.includes(section)) sections.push(section);
    });

    sections.forEach(initFavorites);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
